import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';
import { syncActiveTokensFromCanonical } from '../services/accountRegistry';
import useMediaQuery from '../utils/useMediaQuery';
import './chrome/topHeader.css';

/**
 * Add-another-account sheet. Same email/password login as the main screen, but it
 * signs into an ADDITIONAL account without dropping the current one — on success
 * AuthContext swaps the active tokens and reloads.
 *
 * Oct 14 2026 (top-header + location handoff §4): the Portal panel — black, ringed, round —
 * opened from the dashed `+` in the account menu's footer. Desktop centres it; a phone gets a
 * bottom sheet flush to the edge. Only the surface changed: the sign-in itself, the Google
 * round trip (which snapshots the current account first so it stays switch-back-able), the
 * blocked close while submitting and the "already signed into that account" message are as
 * they were.
 */
export default function AddAccountModal({ onClose }) {
  const { addAccount } = useAuth();
  const phone = useMediaQuery('(max-width: 639px)');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [focused, setFocused] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const canSubmit = email.trim().length > 0 && password.length > 0 && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const r = await addAccount(email.trim(), password);
    if (r.success) {
      if (r.alreadyActive) { setError('You’re already signed into that account.'); setSubmitting(false); }
      // On a real add, AuthContext reloads the page — nothing else to do here.
    } else {
      setError(r.error || 'Could not sign in. Check your email and password.');
      setSubmitting(false);
    }
  };

  const label = { padding: '0 4px', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#8A8A8A' };
  // A field is black until it is focused (the ring appears) or the pair is wrong (solid pink).
  const field = (name) => ({
    height: 48, padding: '0 20px', border: '2px solid transparent', borderRadius: 999, boxSizing: 'border-box',
    background: error
      ? '#161616'
      : focused === name
        ? 'linear-gradient(#161616,#161616) padding-box, var(--th-ring) border-box'
        : '#161616',
    borderColor: error ? '#FF00AA' : 'transparent',
    color: '#fff', fontFamily: 'inherit', fontSize: phone ? 16 : 14, letterSpacing: '.04em',
  });

  return createPortal(
    <div
      className="pr-overlay-root fixed inset-0 safe-inset z-[200] flex justify-center"
      style={{ background: 'rgba(0,0,0,.6)', alignItems: phone ? 'flex-end' : 'center', padding: phone ? 0 : 16, boxSizing: 'border-box' }}
      onClick={() => { if (!submitting) onClose?.(); }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        role="dialog"
        aria-label="Add account"
        data-testid="add-account"
        className="font-mono"
        style={{
          width: '100%', maxWidth: phone ? '100%' : 448, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column', gap: 18,
          padding: phone ? '24px 20px calc(28px + env(safe-area-inset-bottom, 0px))' : 28,
          border: '2px solid transparent', borderRadius: phone ? '28px 28px 0 0' : 28,
          background: 'linear-gradient(#000,#000) padding-box, var(--th-ring) border-box',
          color: '#fff',
        }}
      >
        <div className="flex items-start justify-between" style={{ gap: 16 }}>
          <div className="flex min-w-0 flex-col" style={{ gap: 8 }}>
            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '.12em' }}>ADD ACCOUNT</span>
            <span style={{ fontSize: 12, lineHeight: 1.55, color: '#9CA3AF' }}>Sign into another account. Your current one stays saved so you can switch back anytime.</span>
          </div>
          <button
            type="button"
            onClick={() => { if (!submitting) onClose?.(); }}
            aria-label="Close"
            className="flex flex-shrink-0 items-center justify-center"
            style={{ width: 32, height: 32, border: 0, borderRadius: 999, background: '#1F1F1F', color: '#fff', cursor: 'pointer' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {/* Google — snapshot the current account first so it stays switch-back-able. */}
        <GoogleSignInButton
          redirect="/"
          variant="whitePill"
          label="CONTINUE WITH GOOGLE"
          onBeforeRedirect={syncActiveTokensFromCanonical}
          disabled={submitting}
        />

        <div className="flex items-center" style={{ gap: 12 }}>
          <span style={{ flex: 1, height: 1, background: '#262626' }} />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B6B6B' }}>OR</span>
          <span style={{ flex: 1, height: 1, background: '#262626' }} />
        </div>

        <label className="flex flex-col" style={{ gap: 8 }}>
          <span style={label}>EMAIL</span>
          <input
            id="add-account-email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(null); }}
            onFocus={() => setFocused('email')}
            onBlur={() => setFocused(null)}
            placeholder="you@example.com"
            className="th-input"
            style={field('email')}
          />
        </label>
        <label className="flex flex-col" style={{ gap: 8 }}>
          <span style={label}>PASSWORD</span>
          <input
            id="add-account-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            onFocus={() => setFocused('password')}
            onBlur={() => setFocused(null)}
            placeholder="••••••••"
            className="th-input"
            style={field('password')}
          />
        </label>

        {error && (
          <div role="alert" className="flex items-center" style={{ gap: 10, padding: '12px 16px', borderRadius: 18, background: '#2A0A1C', color: '#FF7AC8', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', lineHeight: 1.45 }}>
            <span aria-hidden style={{ width: 8, height: 8, flexShrink: 0, borderRadius: 999, background: '#FF00AA' }} />
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, paddingTop: 2 }}>
          <button
            type="button"
            onClick={() => { if (!submitting) onClose?.(); }}
            disabled={submitting}
            className="th-add-cancel font-mono"
            style={{ height: 48, border: 0, borderRadius: 999, fontSize: 12, fontWeight: 800, letterSpacing: '.14em' }}
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="font-mono"
            style={{
              height: 48, border: '2px solid transparent', borderRadius: 999,
              background: canSubmit ? 'linear-gradient(#fff,#fff) padding-box, var(--th-ring) border-box' : '#262626',
              color: canSubmit ? '#000' : '#6B6B6B',
              fontSize: 12, fontWeight: 800, letterSpacing: '.14em',
              cursor: canSubmit ? 'pointer' : 'not-allowed', transition: 'background-color .2s, color .2s',
            }}
          >
            {submitting ? 'SIGNING IN…' : 'ADD ACCOUNT'}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
